import { useState } from 'react';

import { Badge, Box, Icon, Stack, Text, Tooltip, useColorModeValue } from '@ecoinvent/ui';
import { BiCommentDetail } from 'react-icons/bi';
import { FiChevronRight } from 'react-icons/fi';

import CopyToClipboardButton from 'components/shared/CopyToClipboardButton';
import TooltipBody from 'components/UI/TooltipBody';
import TooltipHeader from 'components/UI/TooltipHeader';
import { FlowsGroup, FlowsGroupNoWaste, FlowsInput, FlowsOutput, FlowsType } from 'hooks/data/datasets/V4/flows/types';
import { formatNumericalValue } from 'utilities/numbers';

import { groupToIconBackground, groupToIcon, groupToTooltipTitle, groupToTooltipLabel } from './utils/constants';

type Props = {
  flow: FlowsInput | FlowsOutput;
  flowType: FlowsType;
  handleClick: (flowId: string, flowType: FlowsType) => void;
  showComments: boolean;
};

const FlowsViewCard = ({ flow, flowType, handleClick, showComments }: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const bgHoverColor = useColorModeValue('gray.100', 'gray.700');

  const bg = useColorModeValue('white', 'whiteAlpha.100');
  const borderColor = useColorModeValue('transparent', 'whiteAlpha.200');
  const arrowIconColor = useColorModeValue('gray.500', 'gray.400');
  const arrowIconHoverColor = useColorModeValue('green.500', 'green.300');
  const commentColor = useColorModeValue('gray.600', 'gray.400');

  const getTooltipLabel = (group: FlowsGroup, type?: FlowsType): string => {
    if (group === 'waste') {
      return type === 'inputs'
        ? 'This flow represents waste materials received by the process from other systems for treatment, recycling, reuse or disposal.'
        : 'This flow represents a waste material generated by the process that requires disposal, treatment, or management.';
    }
    return groupToTooltipLabel[group as FlowsGroupNoWaste];
  };

  return (
    <Stack
      direction={'row'}
      bg={bg}
      border="1px"
      borderColor={borderColor}
      px={2}
      py={5}
      boxShadow="sm"
      borderRadius="md"
      cursor="pointer"
      onClick={() => handleClick(flow.id, flowType)}
      _hover={{ bg: bgHoverColor }}
      role="group"
    >
      <Stack direction="column" alignItems="flex-start" justifyContent="center" flexGrow={1} spacing={2}>
        <Stack direction="row" alignItems="center" px={3}>
          <Tooltip
            hasArrow
            placement="top-start"
            p={2}
            label={
              <Stack spacing={0}>
                <TooltipHeader>{groupToTooltipTitle[flow.group]}</TooltipHeader>
                <TooltipBody>{getTooltipLabel(flow.group, flowType)}</TooltipBody>
              </Stack>
            }
          >
            <Badge
              colorScheme={groupToIconBackground[flow.group]}
              variant="solid"
              display="flex"
              minWidth="24px"
              maxWidth="24px"
              height="24px"
              flexShrink={0}
              p={0}
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                as={groupToIcon[flow.group]}
                size="xl"
                sx={{
                  '& *': {
                    fill: flow.group === 'valuable' ? 'white' : flow.group === 'waste' ? '#231F1F' : 'auto',
                  },
                }}
              />
            </Badge>
          </Tooltip>
          <Stack direction="column" px={2} flexGrow={1} spacing={0}>
            <Text>{flow.name}</Text>
            {'supplier_count' in flow && flow.supplier_count && (
              <Text fontSize="sm" color="gray.500">
                {flow.supplier_count} supplier{flow.supplier_count > 1 ? 's' : ''}
              </Text>
            )}
          </Stack>
        </Stack>
        {showComments && 'comment' in flow && flow.comment && (
          <Stack
            direction="column"
            spacing={2}
            color="gray.600"
            borderLeft="1px solid"
            borderLeftColor="gray.100"
            pl={2}
            mt={2}
            ml={3}
          >
            <Stack direction="row" alignItems="center">
              <Icon color={commentColor} as={BiCommentDetail} />
              <Text color={commentColor} fontWeight={600}>
                Comments
              </Text>
            </Stack>
            <Stack>
              <Text color={commentColor} overflowWrap="break-word" fontSize={14}>
                {flow.comment}
              </Text>
            </Stack>
          </Stack>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        minWidth="fit-content"
        pr={3}
      >
        <Text whiteSpace="nowrap" textAlign="right">
          {formatNumericalValue(flow.amount)} {flow.unit}{' '}
          <Tooltip hasArrow label="Copy value" placement="top">
            <Box display="inline-block">
              <CopyToClipboardButton value={flow.amount} _groupHover={{ opacity: 1 }} opacity={isHovered ? 1 : 0} />
            </Box>
          </Tooltip>
        </Text>
        <Icon as={FiChevronRight} stroke={arrowIconColor} _groupHover={{ stroke: arrowIconHoverColor }} />
      </Stack>
    </Stack>
  );
};

export default FlowsViewCard;
